import { SlidersHorizontal, BookOpen, GraduationCap, DollarSign, Languages, User } from 'lucide-react';
import {
  EDUCATOR_SUBJECTS,
  GRADE_LEVELS,
  LANGUAGE_CODES,
  TUTOR_GENDERS,
} from '@/lib/schemas/tutor-profile';
import { LANGUAGE_LABELS, GENDER_LABELS } from './constants';
import type { Filters } from './constants';

interface TutorSearchFiltersProps {
  filters: Filters;
  onFilterChange: (key: keyof Filters, value: string) => void;
  onClearFilters: () => void;
  isAnyFilterApplied: boolean;
  isMobile?: boolean;
}

export function TutorSearchFilters({
  filters,
  onFilterChange,
  onClearFilters,
  isAnyFilterApplied,
  isMobile = false,
}: TutorSearchFiltersProps) {
  return (
    <div className={`space-y-5 ${isMobile ? '' : 'rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-5 shadow-sm'}`}>
      {!isMobile && (
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
          <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <SlidersHorizontal className="h-4.5 w-4.5 text-brand-600 dark:text-brand-400" />
            <span>Filter Tutors</span>
          </h3>
          {isAnyFilterApplied && (
            <button
              onClick={onClearFilters}
              className="text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium hover:underline flex items-center gap-1 cursor-pointer"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Subject Select */}
      <div className="space-y-1.5">
        <label htmlFor="subject-select" className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <BookOpen className="h-3.5 w-3.5 text-slate-400" />
          <span>Subject</span>
        </label>
        <select
          id="subject-select"
          value={filters.subject}
          onChange={(e) => onFilterChange('subject', e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:text-slate-100 focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All subjects</option>
          {EDUCATOR_SUBJECTS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Grade Level Select */}
      <div className="space-y-1.5">
        <label htmlFor="grade-select" className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5 text-slate-400" />
          <span>Grade Level</span>
        </label>
        <select
          id="grade-select"
          value={filters.gradeLevel}
          onChange={(e) => onFilterChange('gradeLevel', e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:text-slate-100 focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All grades</option>
          {GRADE_LEVELS.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </div>

      {/* Hourly Rate Range */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5 text-slate-400" />
          <span>Hourly Rate (₹)</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">₹</span>
            <input
              type="number"
              placeholder="Min"
              value={filters.minHourlyInr}
              onChange={(e) => onFilterChange('minHourlyInr', e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 pl-6 pr-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:text-slate-100 focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <span className="text-xs text-slate-400 font-medium">to</span>
          <div className="relative flex-1">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">₹</span>
            <input
              type="number"
              placeholder="Max"
              value={filters.maxHourlyInr}
              onChange={(e) => onFilterChange('maxHourlyInr', e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 pl-6 pr-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none dark:text-slate-100 focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>
      </div>

      {/* Language Spoken */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <Languages className="h-3.5 w-3.5 text-slate-400" />
          <span>Language Spoken</span>
        </label>
        <select
          value={filters.language}
          onChange={(e) => onFilterChange('language', e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:text-slate-100 focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All languages</option>
          {LANGUAGE_CODES.map((code) => (
            <option key={code} value={code}>
              {LANGUAGE_LABELS[code] || code}
            </option>
          ))}
        </select>
      </div>

      {/* Gender Select */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
          <User className="h-3.5 w-3.5 text-slate-400" />
          <span>Educator Gender</span>
        </label>
        <select
          value={filters.gender}
          onChange={(e) => onFilterChange('gender', e.target.value)}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:text-slate-100 focus:ring-1 focus:ring-brand-500"
        >
          <option value="">Any gender</option>
          {TUTOR_GENDERS.map((g) => (
            <option key={g} value={g}>
              {GENDER_LABELS[g] || g}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
